"use client";

import Link from "next/link";
import type { ComponentType, ReactNode } from "react";
import { useState } from "react";
import { SafeBoundary } from "@/components/SafeBoundary";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Select } from "@/components/app/Select";
import { SCORES, type ScoreKind } from "@/lib/scoreGlossary";
import { helpFor, type SectionHelp } from "@/lib/sectionHelp";
import { SectionInfo } from "@/components/app/SectionInfo";
import { SkCard } from "@/components/app/Skeletons";

export function cx(...parts: (string | false | null | undefined)[]) {
  return parts.filter(Boolean).join(" ");
}

export type Tone = "brand" | "good" | "mid" | "bad" | "neutral" | "night" | "sky";

/** One shared score scale everywhere: ≥80 good, 60–79 needs work, <60 poor. */
export function scoreTone(score: number | null | undefined): {
  tone: Tone;
  color: string;
  soft: string;
  label: string;
} {
  if (score == null) return { tone: "neutral", color: "var(--rb-faint)", soft: "var(--rb-surface-2)", label: "No data" };
  if (score >= 80) return { tone: "good", color: "var(--rb-good)", soft: "var(--rb-good-soft)", label: "Strong" };
  if (score >= 60) return { tone: "mid", color: "var(--rb-mid)", soft: "var(--rb-mid-soft)", label: "Needs work" };
  return { tone: "bad", color: "var(--rb-bad)", soft: "var(--rb-bad-soft)", label: "Poor" };
}

const toneClass: Record<Tone, string> = {
  brand: "bg-rb-brand-soft text-rb-brand-deep",
  good: "bg-rb-good-soft text-rb-good",
  mid: "bg-rb-mid-soft text-rb-mid",
  bad: "bg-rb-bad-soft text-rb-bad",
  neutral: "bg-rb-surface-2 text-rb-muted border border-rb-line",
  night: "bg-rb-night text-white",
  sky: "bg-sky-100 text-sky-700",
};

export function Badge({ tone = "neutral", children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return <span className={cx("rb-badge", toneClass[tone], className)}>{children}</span>;
}

export function ScorePill({ score, kind }: { score: number; kind?: ScoreKind }) {
  const t = scoreTone(score);
  const name = kind ? SCORES[kind].name : "Score";
  return (
    <span
      className="rb-num inline-flex h-7 min-w-[44px] items-center justify-center rounded-lg px-2 text-[13px] font-bold"
      style={{ background: t.soft, color: t.color }}
      title={`${name}: ${score}/100 — ${t.label}${kind ? ` · score of ${SCORES[kind].of.toLowerCase()}` : ""}`}
      aria-label={`${name} ${score} out of 100, ${t.label}`}
    >
      {score}
    </span>
  );
}

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "light" | "glass";

export function Btn({
  children,
  variant = "primary",
  size = "md",
  href,
  icon: Icon,
  loading,
  className,
  needs,
  needsMessage = "Fill this in first.",
  blocked,
  ...rest
}: {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: "sm" | "md";
  href?: string;
  icon?: ComponentType<{ size?: number; className?: string }>;
  loading?: boolean;
  className?: string;
  /** The text this button needs (e.g. the URL box). When it's empty the button stays clickable, and a
   *  click explains what's missing and puts the cursor in the empty box instead of doing nothing. */
  needs?: string;
  needsMessage?: string;
  /** Why this can't be used right now (a limit reached, a package wall). The button looks unavailable but stays
   *  clickable, and a click shows this reason instead of doing nothing. Never a silent dead button. */
  blocked?: string | null;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const [missing, setMissing] = useState(false);
  const cls = cx("rb-btn", `rb-btn-${variant}`, size === "sm" && "rb-btn-sm", className);
  const inner = (
    <>
      {loading ? <Loader2 size={size === "sm" ? 14 : 16} className="animate-spin" /> : Icon ? <Icon size={size === "sm" ? 14 : 16} /> : null}
      {children}
    </>
  );
  if (href) {
    return (
      <Link href={href} className={cls}>
        {inner}
      </Link>
    );
  }
  if (blocked) {
    const { onClick: _skip, disabled: _d, ...others } = rest;
    void _skip; void _d;
    return (
      <span className="relative inline-flex">
        <button className={cx(cls, "cursor-not-allowed opacity-55")} aria-disabled="true" {...others}
          onClick={(e) => {
            e.preventDefault();
            setMissing(true);
            window.setTimeout(() => setMissing(false), 6000);
          }}>
          {inner}
        </button>
        {missing && (
          <span role="alert" className="absolute right-0 top-full z-30 mt-1.5 w-max max-w-[300px] rounded-lg bg-rb-night px-2.5 py-1.5 text-left text-[12px] font-semibold leading-snug text-white shadow-[0_10px_24px_-10px_rgba(20,15,51,0.7)]">
            {blocked}
          </span>
        )}
      </span>
    );
  }
  if (needs === undefined) {
    return (
      <button className={cls} disabled={loading || rest.disabled} {...rest}>
        {inner}
      </button>
    );
  }
  const { onClick, ...others } = rest;
  return (
    <span className="relative inline-flex">
      <button className={cls} disabled={loading || rest.disabled} {...others}
        onClick={(e) => {
          if (!needs.trim()) {
            e.preventDefault();
            setMissing(true);
            window.setTimeout(() => setMissing(false), 3500);
            // The empty box next to this button, in the same form or the same group.
            const scope = e.currentTarget.closest("form") ?? e.currentTarget.parentElement?.parentElement?.parentElement;
            const box = [...(scope?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input:not([type=checkbox]):not([type=hidden]), textarea") ?? [])].find((x) => !x.value.trim());
            box?.focus();
            return;
          }
          setMissing(false);
          onClick?.(e);
        }}>
        {inner}
      </button>
      {missing && (
        <span role="alert" className="absolute right-0 top-full z-30 mt-1.5 w-max max-w-[260px] rounded-lg bg-rb-night px-2.5 py-1.5 text-[12px] font-semibold text-white shadow-[0_10px_24px_-10px_rgba(20,15,51,0.7)]">
          {needsMessage}
        </span>
      )}
    </span>
  );
}

export function Card({
  title,
  subtitle,
  icon: Icon,
  action,
  children,
  className,
  bodyClassName,
  id,
  info,
}: {
  id?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: ComponentType<{ size?: number; className?: string }>;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** The ⓘ explanation; by default looked up by title in lib/sectionHelp.ts. */
  info?: SectionHelp | null;
}) {
  const help = info === undefined ? helpFor(title) : info;
  return (
    <section id={id} className={cx("rb-card flex min-w-0 flex-col", id && "scroll-mt-24", className)}>
      {(title || action) && (
        <header className="rb-card-head">
          <div className="flex min-w-0 items-center gap-3">
            {Icon && (
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-rb-brand-soft text-rb-brand-deep">
                <Icon size={16} />
              </span>
            )}
            <div className="min-w-0">
              {title && (
                <h2 className="rb-card-title flex min-w-0 items-center gap-1.5">
                  <span className="truncate">{title}</span>
                  {help && <SectionInfo help={help} label={typeof title === "string" ? title : "About this section"} />}
                </h2>
              )}
              {subtitle && <p className="rb-sub line-clamp-2">{subtitle}</p>}
            </div>
          </div>
          {action}
        </header>
      )}
      <div className={cx("min-w-0 flex-1", bodyClassName ?? "p-5")}>
        {/* one broken card never takes the page down */}
        <SafeBoundary name={typeof title === "string" ? title : id ?? "Card"}>{children}</SafeBoundary>
      </div>
    </section>
  );
}

/**
 * A loading placeholder, shaped like what's coming:
 *   - kind="chart": bars over grid lines, for a chart inside a card;
 *   - kind="rows":  list rows (icon + two lines), for a list inside a card;
 *   - kind="card" (the default for 200px or taller): a whole card with a header and rows;
 *   - otherwise a single shimmering bar.
 * For a page's main loading state, build its real layout from the pieces in Skeletons.tsx.
 */
export function Skeleton({ className, kind }: { className?: string; kind?: "card" | "chart" | "rows" | "block" }) {
  const px = Number(/(?:^|\s)h-\[(\d+)px\]/.exec(className ?? "")?.[1] ?? 0);
  const k = kind ?? (px >= 200 ? "card" : "block");
  if (k === "card") return <SkCard className={className} rows={Math.max(2, Math.floor(((px || 280) - 72) / 57))} />;
  if (k === "chart") {
    const bars = [42, 64, 50, 78, 58, 88, 70, 92, 66, 80, 60, 74];
    return (
      <div aria-hidden className={cx("relative flex items-end gap-[3%] overflow-hidden px-1 pb-6 pt-3", className)}>
        <div className="pointer-events-none absolute inset-x-1 bottom-6 top-3 flex flex-col justify-between">{[0, 1, 2, 3].map((i) => <span key={i} className="h-px bg-rb-line/70" />)}</div>
        {bars.map((h, i) => <span key={i} className="rb-skeleton relative flex-1 !rounded-t-md !rounded-b-none" style={{ height: `${h}%` }} />)}
        <div className="absolute inset-x-1 bottom-0 flex justify-between">{[0, 1, 2, 3, 4].map((i) => <span key={i} className="rb-skeleton h-2 w-8 rounded-full opacity-60" />)}</div>
      </div>
    );
  }
  if (k === "rows") {
    const n = Math.max(2, Math.floor(((px || 200) + 8) / 56));
    return (
      <div aria-hidden className={cx("flex flex-col overflow-hidden", className)}>
        {Array.from({ length: n }, (_, i) => (
          <div key={i} className="flex items-center gap-3 py-2.5">
            <span className="rb-skeleton h-9 w-9 flex-shrink-0 rounded-xl" />
            <span className="min-w-0 flex-1 space-y-2">
              <span className={cx("rb-skeleton block h-3 rounded-full", ["w-3/5", "w-2/5", "w-1/2", "w-2/3"][i % 4])} />
              <span className="rb-skeleton block h-2.5 w-1/3 rounded-full opacity-70" />
            </span>
            <span className="rb-skeleton h-6 w-14 flex-shrink-0 rounded-lg" />
          </div>
        ))}
      </div>
    );
  }
  return <div aria-hidden className={cx("rb-skeleton", className)} />;
}

export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
  compact,
}: {
  icon: ComponentType<{ size?: number; className?: string }>;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={cx("flex flex-col items-center justify-center text-center", compact ? "py-8" : "py-14")}>
      <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-rb-brand-soft text-rb-brand-deep">
        <Icon size={22} />
      </span>
      <p className="rb-display text-[15px] font-bold">{title}</p>
      {body && <p className="mt-1 max-w-[42ch] text-[13px] text-rb-muted">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Meter({ value, max, color = "var(--rb-brand)", className }: { value: number; max: number; color?: string; className?: string }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className={cx("h-2 w-full overflow-hidden rounded-full bg-rb-surface-2", className)}>
      <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export function ErrorNote({ children, onRetry }: { children: ReactNode; onRetry?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-rb-bad/25 bg-rb-bad-soft px-4 py-3 text-[13px] font-medium text-rb-bad">
      <span>{children}</span>
      {onRetry && (
        <button onClick={onRetry} className="font-semibold underline underline-offset-2">
          Try again
        </button>
      )}
    </div>
  );
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return "—";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} d ago`;
  return new Date(iso).toLocaleDateString();
}

export function fmtNum(n: number | null | undefined): string {
  // A missing number from the server shows a dash instead of breaking the page.
  if (n == null || typeof n !== "number" || Number.isNaN(n)) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString();
}

/** Page numbers to show: always the first and last, the current one and its
 *  neighbours, with gaps marked by null. */
function pageList(current: number, pages: number): (number | null)[] {
  const want = new Set([0, pages - 1, current - 1, current, current + 1]);
  const out: (number | null)[] = [];
  for (let i = 0; i < pages; i++) {
    if (!want.has(i)) continue;
    if (out.length && (out[out.length - 1] as number) < i - 1) out.push(null);
    out.push(i);
  }
  return out;
}

/** Pagination used by every list in the dashboard: "1–10 of 84", numbered
 *  pages on wider screens, previous/next everywhere, optional page size. */
export function Pagination({
  page,
  pageSize,
  total,
  onPage,
  onPageSize,
  sizes = [10, 25, 50],
  className,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize?: (size: number) => void;
  sizes?: number[];
  className?: string;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pages - 1);
  const smallest = onPageSize ? Math.min(pageSize, ...sizes) : pageSize;
  if (total <= smallest) return null;
  const go = (p: number) => onPage(Math.max(0, Math.min(pages - 1, p)));
  return (
    <nav aria-label="Pagination" className={cx("flex flex-wrap items-center justify-between gap-3 border-t border-rb-line px-4 py-3 text-[12.5px] text-rb-muted", className)}>
      <span className="rb-num">
        <strong className="font-semibold text-rb-ink">{current * pageSize + 1}–{Math.min(total, (current + 1) * pageSize)}</strong> of {total.toLocaleString()}
      </span>
      <div className="flex items-center gap-1">
        <button type="button" className="rb-btn rb-btn-secondary rb-btn-sm !h-8 !px-2" disabled={current === 0} onClick={() => go(current - 1)} aria-label="Previous page">
          <ChevronLeft size={16} />
        </button>
        <span className="hidden items-center gap-1 sm:flex">
          {pageList(current, pages).map((p, i) =>
            p === null ? (
              <span key={`gap${i}`} className="px-1 text-rb-faint">…</span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => go(p)}
                aria-current={p === current ? "page" : undefined}
                className={cx(
                  "rb-num h-8 min-w-[32px] rounded-lg px-2 text-[12.5px] font-semibold transition-colors",
                  p === current ? "bg-rb-night text-white" : "text-rb-muted hover:bg-rb-surface-2 hover:text-rb-ink",
                )}
              >
                {p + 1}
              </button>
            ),
          )}
        </span>
        <span className="rb-num px-2 font-semibold text-rb-ink sm:hidden">
          {current + 1} / {pages}
        </span>
        <button type="button" className="rb-btn rb-btn-secondary rb-btn-sm !h-8 !px-2" disabled={current >= pages - 1} onClick={() => go(current + 1)} aria-label="Next page">
          <ChevronRight size={16} />
        </button>
      </div>
      {onPageSize && (
        <label className="flex items-center gap-2">
          <span className="hidden sm:inline">Rows</span>
          <Select
            className="h-8 rounded-lg border border-rb-line bg-white px-2 text-[12.5px] font-semibold text-rb-ink"
            value={pageSize}
            onChange={(e) => {
              onPageSize(Number(e.target.value));
              onPage(0);
            }}
            aria-label="Rows per page"
          >
            {sizes.map((n) => <option key={n} value={n}>{n}</option>)}
          </Select>
        </label>
      )}
    </nav>
  );
}

/** Client-side paging over a list that is already loaded. */
export function usePaged<T>(items: T[], initialSize = 10) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialSize);
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  const current = Math.min(page, pages - 1);
  return {
    visible: items.slice(current * pageSize, current * pageSize + pageSize),
    pager: { page: current, pageSize, total: items.length, onPage: setPage, onPageSize: setPageSize },
    reset: () => setPage(0),
  };
}

/** A row of pill buttons for switching between views or filters. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  className,
  size = "md",
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode; count?: number }[];
  className?: string;
  size?: "sm" | "md";
}) {
  return (
    // No scrolling box: every option always shows in full, wrapping on narrow screens.
    <div className={cx("max-w-full", className)}>
      <div className="inline-flex max-w-full flex-wrap gap-1 rounded-xl bg-rb-surface-2 p-1" role="tablist">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={value === o.value}
            onClick={() => onChange(o.value)}
            className={cx(
              "inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg font-semibold transition-colors",
              size === "sm" ? "h-7 px-2.5 text-[12px]" : "h-8 px-3 text-[13px]",
              value === o.value ? "bg-white text-rb-ink shadow-sm" : "text-rb-muted hover:text-rb-ink",
            )}
          >
            {o.label}
            {o.count != null && (
              <span className={cx("rb-num rounded-md px-1.5 text-[11px]", value === o.value ? "bg-rb-brand-soft text-rb-brand-deep" : "bg-white/70 text-rb-muted")}>{o.count}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/** A list that pages itself once it grows past `size` items. */
export function PagedList<T>({
  items,
  render,
  size = 10,
  className,
  pagerClassName,
  fixed,
}: {
  items: T[];
  render: (item: T, index: number) => ReactNode;
  size?: number;
  className?: string;
  pagerClassName?: string;
  /** No rows-per-page choice: for narrow side columns. */
  fixed?: boolean;
}) {
  const { visible, pager } = usePaged(items, size);
  return (
    <>
      <ul className={className}>{visible.map((item, i) => render(item, pager.page * pager.pageSize + i))}</ul>
      <Pagination {...pager} onPageSize={fixed ? undefined : pager.onPageSize} sizes={[size, size * 2, size * 5]} className={pagerClassName} />
    </>
  );
}
